import { useUser } from "@/components/UserProvider";
import {
  Badge,
  FirmProfilePhoto,
  Input,
  Label,
  Typography,
} from "@/components/ui";
import { UserProfilePhoto } from "@/components/ui/UserProfilePhoto/UserProfilePhoto";
import {
  useUpdateFirmProfilePicture,
  useUpdateUserProfilePicture,
} from "@/lib/data/react-query";
import type { ChangeEvent } from "react";

export function ProfilePhotosStep() {
  const { currentUser } = useUser();
  const updateUserProfilePicture = useUpdateUserProfilePicture();
  const updateFirmProfilePicture = useUpdateFirmProfilePicture();
  const firm = currentUser.firm;
  const hasProfilePhoto = Boolean(currentUser.profilePicturePresignedUrlKey);
  const hasFirmLogo = Boolean(firm.profilePicturePresignedUrlKey);
  const isUploadingProfile = updateUserProfilePicture.isPending;
  const isUploadingLogo = updateFirmProfilePicture.isPending;

  const handleProfilePictureUpload = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      await updateUserProfilePicture.mutateAsync({
        user: currentUser,
        file,
      });
    } catch (error) {
      console.error("Error updating profile picture:", error);
    } finally {
      event.target.value = "";
    }
  };

  const handleFirmLogoUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      await updateFirmProfilePicture.mutateAsync({
        firm,
        file,
      });
    } catch (error) {
      console.error("Error updating firm logo:", error);
    } finally {
      event.target.value = "";
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <section className="space-y-6">
          <div className="space-y-2">
            <Typography tag="h3" variant="heading" className="text-lg">
              Add your profile photo
            </Typography>
            <Typography className="text-muted-foreground text-sm">
              This will show on your dashboard, invoices, and messages.
            </Typography>
          </div>
          <div className="bg-muted/40 border-border relative flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed px-6 py-10 text-center">
            <Input
              type="file"
              accept="image/*"
              onChange={handleProfilePictureUpload}
              className="hidden"
              id="onboarding-profile-photo-upload"
              disabled={isUploadingProfile}
            />
            <Label
              htmlFor="onboarding-profile-photo-upload"
              aria-disabled={isUploadingProfile}
              className={`focus-visible:ring-primary focus-visible:ring-offset-background absolute inset-0 rounded-2xl focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden ${
                isUploadingProfile ? "cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              <span className="sr-only">Upload profile photo</span>
            </Label>
            <div
              className={
                hasProfilePhoto
                  ? "pointer-events-none flex h-24 w-24 items-center justify-center"
                  : "bg-background border-border pointer-events-none flex h-24 w-24 items-center justify-center rounded-full border"
              }
            >
              <UserProfilePhoto
                user={currentUser}
                className="h-24 w-24"
                initialsClassName="text-lg"
              />
            </div>
            <div className="space-y-1">
              <Typography tag="p" weight="semibold">
                Click to upload a photo
              </Typography>
            </div>
            <div className="space-y-1">
              <Typography className="text-muted-foreground text-xs">
                For optimal results, use:
              </Typography>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Badge variant="secondary">PNG</Badge>
                <Badge variant="secondary">JPG</Badge>
              </div>
            </div>
            {isUploadingProfile && (
              <Typography className="text-primary text-sm">
                Uploading...
              </Typography>
            )}
          </div>
        </section>
        <section className="space-y-6">
          <div className="space-y-2">
            <Typography tag="h3" variant="heading" className="text-lg">
              Add your firm logo
            </Typography>
            <Typography className="text-muted-foreground text-sm">
              This appears on client communications and documents.
            </Typography>
          </div>
          <div className="bg-muted/40 border-border relative flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed px-6 py-10 text-center">
            <Input
              type="file"
              accept="image/*"
              onChange={handleFirmLogoUpload}
              className="hidden"
              id="onboarding-firm-logo-upload"
              disabled={isUploadingLogo}
            />
            <Label
              htmlFor="onboarding-firm-logo-upload"
              aria-disabled={isUploadingLogo}
              className={`focus-visible:ring-primary focus-visible:ring-offset-background absolute inset-0 rounded-2xl focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden ${
                isUploadingLogo ? "cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              <span className="sr-only">Upload firm logo</span>
            </Label>
            <div
              className={
                hasFirmLogo
                  ? "pointer-events-none flex h-24 w-40 items-center justify-center"
                  : "bg-background border-border flex h-24 w-40 items-center justify-center rounded-xl border"
              }
            >
              {hasFirmLogo ? (
                <FirmProfilePhoto
                  firmName={firm.name}
                  firmProfilePicturePresignedUrlKey={
                    firm.profilePicturePresignedUrlKey ?? undefined
                  }
                  className="h-24 w-40"
                  showFallbackLogo={false}
                />
              ) : (
                <Typography className="text-muted-foreground text-xs">
                  320 x 200
                </Typography>
              )}
            </div>
            <div className="space-y-1">
              <Typography tag="p" weight="semibold">
                Click to upload a logo
              </Typography>
            </div>
            <div className="space-y-1">
              <Typography className="text-muted-foreground text-xs">
                For optimal results, use:
              </Typography>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Badge variant="secondary">PNG</Badge>
                <Badge variant="secondary">JPG</Badge>
                <Badge variant="secondary">Transparent background</Badge>
              </div>
            </div>
            {isUploadingLogo && (
              <Typography className="text-primary text-sm">
                Uploading...
              </Typography>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
